import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component, CUSTOM_ELEMENTS_SCHEMA,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import {
    ActionPreviewEdits,
    ActionPreviewRecipientItem,
    ActionPreviewRecipientsSection,
} from '../../../../../services/gable/gable.service';
import { moneyColumns, PickerColumn, UnitPickerComponent } from '../../../../shared/unit-picker/unit-picker.component';

/**
 * `recipients` section of an action-preview card.
 *
 * UX model: the unit picker IS the recipient list. Existing
 * recipients come back pre-checked via `preselectedUnitIds`; the user
 * can check / uncheck inline to adjust the batch. There's no separate
 * "Recipients · N units" list above and no "+ Add unit" toggle — the
 * picker is always visible, source of truth for who's getting the
 * action when the user clicks Create.
 *
 * Selection changes emit a diff (`added_ids` + `removed_ids`) on the
 * recipients edit slot. The host card stuffs that into pendingEdits and
 * commit-action folds it into `params.unit_ids` server-side before
 * executing — see `SendViolationNoticeAction::execute`.
 */
@Component({
    selector: 'app-ap-recipients-section',
    templateUrl: './recipients-section.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, UnitPickerComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ActionPreviewRecipientsSectionComponent implements OnInit, OnChanges {
    @Input({ required: true }) section!: ActionPreviewRecipientsSection;
    @Input() pendingEdit: ActionPreviewEdits['recipients'] | null = null;

    /** Emits the next `recipients` edit delta. Host merges into pendingEdits. */
    @Output() readonly edit = new EventEmitter<NonNullable<ActionPreviewEdits['recipients']>>();

    /** Kept for forward compat (Restore skipped, etc.); add_unit is no longer routed here. */
    @Output() readonly listAction = new EventEmitter<string>();

    /** Locally-tracked size of the picker's current selection — used for the
     *  header count + the primary button label override (TODO when wired). */
    selectionCount = 0;

    readonly unitColumns: PickerColumn[] = ['address', 'tags', ...moneyColumns('balance', 'pastDue')];

    /**
     * Stable array reference derived from section.items. A getter would
     * create a new array on every CD cycle, causing Angular to see a
     * reference change every render and trigger ngOnChanges in the child
     * unit picker, which resets its selection to the preselected ids.
     */
    existingUnitIds: number[] = [];

    constructor(private _cdr: ChangeDetectorRef) {}

    ngOnInit(): void {
        this.existingUnitIds = this._computeExistingUnitIds();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['section']) {
            this.existingUnitIds = this._computeExistingUnitIds();
        }
    }

    private _computeExistingUnitIds(): number[] {
        const ids = (this.section?.items ?? [])
            .map((item) => {
                const m = /^unit-(\d+)$/.exec(item.id);
                return m ? Number(m[1]) : null;
            })
            .filter((id): id is number => id !== null);
        return ids;
    }

    trackByItemId(_: number, item: ActionPreviewRecipientItem): string {
        return item.id;
    }

    /**
     * Re-emit the recipients edit delta whenever the picker's selection
     * changes. Diffs against `existingUnitIds` so we only carry the
     * changes the user actually made:
     *   - checked ids not in the existing set → `added_ids`
     *   - existing ids that the user has unchecked → `removed_ids`
     */
    onSelectionChange(unitIds: number[]): void {
        const existing = new Set(this.existingUnitIds);
        const selected = new Set(unitIds);

        const addedIds = unitIds
            .filter((id) => !existing.has(id))
            .map((id) => `unit-${id}`);

        const removedIds = Array.from(existing)
            .filter((id) => !selected.has(id))
            .map((id) => `unit-${id}`);

        this.selectionCount = selected.size;

        const next: NonNullable<ActionPreviewEdits['recipients']> = { ...this.pendingEdit };
        if (addedIds.length > 0) {
            next.added_ids = addedIds;
        } else {
            delete next.added_ids;
        }
        if (removedIds.length > 0) {
            next.removed_ids = removedIds;
        } else {
            delete next.removed_ids;
        }
        this.edit.emit(next);
        this._cdr.markForCheck();
    }

    /**
     * Number of currently-selected units for the header label. Falls
     * back to the existing unit_ids count before the user has touched
     * the picker.
     */
    activeCount(): number {
        return this.selectionCount > 0 ? this.selectionCount : this.existingUnitIds.length;
    }

    /** Only named people, no units: list them as rows instead of opening the whole unit picker. */
    get peopleOnly(): boolean {
        return this.existingUnitIds.length === 0 && (this.section?.items ?? []).length > 0;
    }

    countLabel(): string {
        if (this.peopleOnly) {
            const n = (this.section?.items ?? []).filter((item) => !this.isRemoved(item)).length;
            return `${n} ${n === 1 ? 'person' : 'people'}`;
        }
        const n = this.activeCount();
        return `${n} ${n === 1 ? 'unit' : 'units'}`;
    }

    /** Units beyond the rendered `items` list (bulk previews cap at 100 — SD-9212); still part of the action's target set server-side. */
    get overflowCount(): number {
        const total = this.section?.totalCount;
        if (typeof total !== 'number') {
            return 0;
        }
        return Math.max(total - (this.section?.items?.length ?? 0), 0);
    }

    /**
     * True when the rendered items are only part of the target set. The full
     * picker preselects just the visible ids, so with overflow present it would
     * silently show the other N as unchecked even though they're still targeted —
     * swap to the truthful per-row list instead (SD-9212 v1).
     */
    get isOverflowing(): boolean {
        return this.overflowCount > 0;
    }

    isRemoved(item: ActionPreviewRecipientItem): boolean {
        return item.removed === true || (this.pendingEdit?.removed_ids ?? []).includes(item.id);
    }

    toggleRemove(item: ActionPreviewRecipientItem): void {
        const removedIds = new Set(this.pendingEdit?.removed_ids ?? []);
        const wasRemoved = item.removed === true || removedIds.has(item.id);
        if (wasRemoved) {
            removedIds.delete(item.id);
        } else {
            removedIds.add(item.id);
        }

        const next: NonNullable<ActionPreviewEdits['recipients']> = { ...this.pendingEdit };
        if (removedIds.size > 0) {
            next.removed_ids = Array.from(removedIds);
        } else {
            delete next.removed_ids;
        }
        this.edit.emit(next);
        this._cdr.markForCheck();
    }
}
